import { isOnline } from "@/offline/online-status";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown) {
    super(`API error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * The server's own reason for a failed request, when it sent one: DRF's
 * `detail`, else the first field error (`{"email": ["…"]}`). An `ApiError`'s
 * `.message` is only "API error {status}", so anything else — a 500 with an
 * HTML body, a network failure — gets the caller's `fallback`.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.body && typeof error.body === "object") {
    const body = error.body as Record<string, unknown>;
    if (typeof body.detail === "string") return body.detail;
    for (const value of Object.values(body)) {
      if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    }
  }
  return fallback;
}

/**
 * Thrown by `apiFetch` / `apiFetchMultipart` when `navigator.onLine` is false
 * at call time. Surfaces synchronously so callers don't wait the full WorkOS
 * `getAccessToken()` timeout (~30s) before TanStack Query falls back to the
 * persisted cache. The guard fires before any auth or network work.
 *
 * Also thrown when `getAccessToken()` itself fails while the browser is
 * offline — e.g. WorkOS's refresh ticker gave up retrying during an offline
 * session and wiped the in-memory access token, leaving `getAccessToken()`
 * to throw `LoginRequiredError`. We re-classify that as offline so the
 * retry policy short-circuits and the persisted cache is served.
 */
export class OfflineError extends Error {
  path: string;

  constructor(path: string) {
    super(`Offline: ${path}`);
    this.name = "OfflineError";
    this.path = path;
  }
}

async function getTokenOrOfflineError(
  path: string,
  getAccessToken: () => Promise<string>,
): Promise<string> {
  try {
    return await getAccessToken();
  } catch (err) {
    // If we've lost connectivity between the up-front isOnline() check and now,
    // or if WorkOS entered ERROR state during a failed offline refresh and
    // wiped the in-memory access token, re-classify as OfflineError. Genuine
    // auth failures while online (revoked session, etc.) keep propagating.
    if (!isOnline()) throw new OfflineError(path);
    throw err;
  }
}

export async function apiFetch<T>(
  path: string,
  getAccessToken: () => Promise<string>,
  init?: RequestInit,
): Promise<T> {
  if (!isOnline()) throw new OfflineError(path);

  const token = await getTokenOrOfflineError(path, getAccessToken);
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Public fetch for share-token endpoints that do NOT require a session but
 * accept an optional bearer if the visitor happens to be signed in (mirrors
 * the old `shareOtusRequest`). Used only by the public share page, which is
 * exempt from the auth gate.
 *
 * `getAccessToken` is optional. When provided, it is awaited inside a
 * try/catch: a resolved token is attached as `Authorization: Bearer …`; any
 * failure (anonymous visitor, expired refresh) is swallowed and the request
 * proceeds anonymously. Unlike {@link apiFetch}, this never throws
 * `OfflineError` — the share view is online-only and has no persisted cache to
 * fall back to, so a network failure surfaces as a normal fetch rejection /
 * `ApiError` for the caller's query to handle.
 */
export async function apiFetchPublic<T>(
  path: string,
  getAccessToken?: () => Promise<string>,
  init?: RequestInit,
): Promise<T> {
  let token: string | null = null;
  if (getAccessToken) {
    try {
      token = await getAccessToken();
    } catch {
      // Anonymous visitor (or refresh expired) — proceed without auth.
      token = null;
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Typed multipart/form-data wrapper. Do NOT set Content-Type manually — the
 * browser must set it so the multipart boundary is included automatically.
 *
 * Extra request headers (e.g. the outbox's `X-Idempotency-Key`) may be passed
 * via `init.headers`; they are merged after `Authorization` and so cannot
 * clobber it. `Content-Type` must still never be supplied here.
 */
export async function apiFetchMultipart<T>(
  path: string,
  formData: FormData,
  getAccessToken: () => Promise<string>,
  init?: Omit<RequestInit, "body"> & { headers?: Record<string, string> },
): Promise<T> {
  if (!isOnline()) throw new OfflineError(path);

  const token = await getTokenOrOfflineError(path, getAccessToken);
  // Defensively strip any caller-supplied `Content-Type` (case-insensitive)
  // — the browser MUST set it so the multipart boundary is generated. A
  // caller passing one would break the request body parsing server-side.
  const callerHeaders: Record<string, string> = { ...init?.headers };
  for (const key of Object.keys(callerHeaders)) {
    if (key.toLowerCase() === "content-type") delete callerHeaders[key];
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    method: init?.method ?? "POST",
    headers: {
      ...callerHeaders,
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}
